
// src/lib/api.ts
import { toast } from "@/components/ui/use-toast";
import { auth } from "@/lib/firebase";
import { decrementScanCount } from "@/lib/firebase";
import { scanQuotaService } from "@/services/scanQuotaService";

const POKEMON_TCG_API_URL = "https://api.pokemontcg.io/v2";
// Pokemon TCG API key placeholder – replace with your actual key
const POKEMON_TCG_API_KEY = "API_KEY_POKEMON_TCG";

// Controller to cancel stale searches
let lastSearchController: AbortController | null = null;

export interface PokemonCard {
  id: string;
  name: string;
  images: { small: string; large: string };
  cardmarket?: {
    prices: {
      averageSellPrice: number;
      lowPrice: number;
      trendPrice: number;
      germanProLow: number;
      suggestedPrice: number;
      reverseHoloSell: number;
      reverseHoloLow: number;
      reverseHoloTrend: number;
      lowPriceExPlus: number;
      avg1: number;
      avg7: number;
      avg30: number;
      reverseHoloAvg1: number;
      reverseHoloAvg7: number;
      reverseHoloAvg30: number;
    };
    updatedAt: string;
  };
  tcgplayer?: {
    url: string;
    updatedAt: string;
    prices: Record<string, {
      low: number;
      mid: number;
      high: number;
      market: number;
      directLow: number;
    }>;
  };
  rarity?: string;
  set: {
    id: string;
    name: string;
    series: string;
    printedTotal: number;
    total: number;
    releaseDate: string;
    images: { symbol: string; logo: string };
  };
  number: string;
  artist?: string;
  flavorText?: string;
}

interface SearchOptions {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  q?: string;
}

/**
 * Search for cards. Cancels any prior in‐flight request so only the latest returns.
 */
export async function searchCards(
  options: SearchOptions = {}
): Promise<{ data: PokemonCard[]; totalCount: number }> {
  const { page = 1, pageSize = 20, orderBy = "", q = "" } = options;

  // 1) Cancel previous
  if (lastSearchController) {
    lastSearchController.abort();
  }
  const controller = new AbortController();
  lastSearchController = controller;

  // 2) Build params
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });
  if (orderBy) params.append("orderBy", orderBy);

  // 3) Normalize `q` to avoid 400s
  const raw = q.trim();
  if (raw) {
    let formatted = raw;
    if (/^\d+$/.test(raw)) {
      formatted = `number:"${raw}"`;
    } else if (!/^\w+\s*:/.test(raw)) {
      const esc = raw.replace(/"/g, '\\"');
      formatted = `name:"${esc}"`;
    }
    params.append("q", formatted);
  }

  try {
    // 4) Fetch with abort signal
    const res = await fetch(
      `${POKEMON_TCG_API_URL}/cards?${params.toString()}`,
      {
        headers: { "X-Api-Key": POKEMON_TCG_API_KEY },
        signal: controller.signal,
      }
    );

    // 5) If aborted, throw to skip
    if (controller.signal.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }

    const json = await res.json();

    // 6) One more abort check
    if (controller.signal.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    return {
      data: json.data,
      totalCount: json.totalCount || 0,
    };
  } catch (err: any) {
    if (err.name === "AbortError") {
      // silent drop
      throw err;
    }
    console.error("Error fetching Pokemon cards:", err);
    toast({
      title: "Error",
      description: "Failed to fetch Pokemon cards. Please try again later.",
      variant: "destructive",
    });
    return { data: [], totalCount: 0 };
  }
}

export async function getCardById(id: string): Promise<PokemonCard | null> {
  try {
    console.log(`Fetching details for card: ${id}`);
    const res = await fetch(`${POKEMON_TCG_API_URL}/cards/${id}`, {
      headers: { "X-Api-Key": POKEMON_TCG_API_KEY },
    });
    
    if (!res.ok) {
      console.error(`API error ${res.status}: ${res.statusText}`);
      throw new Error(`API error: ${res.status}`);
    }
    
    const json = await res.json();
    console.log(`Successfully fetched card details for: ${json.data.name}`);
    return json.data;
  } catch (err) {
    console.error("Error fetching card details:", err);
    toast({
      title: "Error",
      description: "Failed to fetch card details. Please try again later.",
      variant: "destructive",
    });
    return null;
  }
}

export async function getCardSets(options: SearchOptions = {}): Promise<any> {
  try {
    const { page = 1, pageSize = 20, orderBy = "" } = options;
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (orderBy) params.append("orderBy", orderBy);

    const res = await fetch(
      `${POKEMON_TCG_API_URL}/sets?${params.toString()}`,
      { headers: { "X-Api-Key": POKEMON_TCG_API_KEY } }
    );
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error("Error fetching card sets:", err);
    toast({
      title: "Error",
      description: "Failed to fetch card sets. Please try again later.",
      variant: "destructive",
    });
    return { data: [] };
  }
}

export async function searchCardByImage(imageBlob: Blob): Promise<string | null> {
  try {
    console.log("Starting card scan process...");
    console.log("Image blob size:", imageBlob.size, "bytes");
    
    const currentUser = auth.currentUser;
    
    // Check scan availability through scanQuotaService
    const scanQuotaAvailable = await scanQuotaService.hasAvailableScans();
    console.log("Scan availability:", scanQuotaAvailable);
    
    if (!scanQuotaAvailable) {
      const message = !currentUser 
        ? "No trial scans remaining. Sign up for 3 more free scans!"
        : "No scans remaining. Subscribe for more scans.";
        
      toast({
        title: "Scan Limit Reached",
        description: message,
        variant: "destructive",
      });
      return null;
    }
    
    // Create form data to send to our Firebase Cloud Function
    const formData = new FormData();
    formData.append("image", imageBlob);

    // Get authentication token if user is logged in
    let headers: Record<string, string> = {};
    if (currentUser) {
      try {
        // Use cached token when possible to avoid excessive refreshes
        const token = await currentUser.getIdToken(false);
        headers.Authorization = `Bearer ${token}`;
        console.log("Auth token obtained for API call");
      } catch (error) {
        console.error("Failed to get auth token:", error);
      }
    }

    console.log("Sending request to /api/scanCard endpoint...");
    
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    try {
      // Call our Firebase Cloud Function
      const scanResponse = await fetch('/api/scanCard', {
        method: "POST",
        headers,
        body: formData,
        signal: controller.signal,
      });

      console.log("Received response:", scanResponse.status, scanResponse.statusText);
      
      if (!scanResponse.ok) {
        // Try to parse the error message
        let errorMessage = "Error processing card image";
        let errorJson;
        
        try {
          errorJson = await scanResponse.json();
          errorMessage = errorJson.error || errorMessage;
        } catch (e) {
          // If we can't parse the JSON, use the status text
          errorMessage = `Server error: ${scanResponse.status} ${scanResponse.statusText}`;
        }
        
        console.error("Scan error:", errorMessage, errorJson || "");
        toast({
          title: "Scan Error",
          description: errorMessage,
          variant: "destructive",
        });
        
        return null;
      }

      const result = await scanResponse.json();
      console.log("Scan result:", result);
      
      if (!result.success) {
        if (result.error) {
          toast({
            title: "Scan Failed",
            description: result.error,
            variant: "destructive",
          });
        }
        return null;
      }

      // Invalidate quota cache after scan to force refresh
      scanQuotaService.invalidateCache();

      return result.cardId;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (err: any) {
    if (err.name === 'AbortError') {
      console.error("Scan request timed out");
      toast({
        title: "Timeout",
        description: "Card scan request timed out. Please try again with a better connection.",
        variant: "destructive",
      });
    } else {
      console.error("Error identifying card:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to identify card from image. Please try again.",
        variant: "destructive",
      });
    }
    return null;
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result?.toString().split(",")[1];
      resolve(b64 || "");
    };
    reader.onerror = () => reject(new Error("Failed to convert image"));
    reader.readAsDataURL(blob);
  });
}

export { getUserSubscriptionData } from '@/lib/firebase';
